'use client'

import { useEffect, useState } from 'react'

import {
  buildVisibleRangeProfileRequest,
  resolveVisibleRangeProfileBucketCount,
} from '@/features/chart/lib/visible-range-profile'
import type { VisibleRangeProfileDisplaySettings } from '@/features/chart/lib/visible-range-profile-studies'
import type { TVWidgetInstance } from '@/lib/tradingview/types/tv-widget.types'
import { useVisibleRangeProfile } from '@/service/chart.hook'
import type { Exchange } from '@/types/generated/common/types'
import type { VisibleRangeProfileRequest } from '@/types/generated/public_api'

const RANGE_DEBOUNCE_MS = 400
const PRICE_RANGE_POLL_MS = 500

function requestKey(request: VisibleRangeProfileRequest | null) {
  if (!request) return ''
  return [
    request.exchange,
    request.symbol,
    request.fromMs,
    request.toMs,
    request.priceMin ?? '',
    request.priceMax ?? '',
    request.buckets,
  ].join(':')
}

export function useVisibleRangeProfileViewport({
  widget,
  isChartReady,
  enabled,
  exchange,
  symbol,
  includePriceRange,
  settings,
  tickSize,
}: {
  widget: TVWidgetInstance | null
  isChartReady: boolean
  enabled: boolean
  exchange: Exchange
  symbol: string
  includePriceRange: boolean
  settings: readonly VisibleRangeProfileDisplaySettings[]
  tickSize?: number
}) {
  const [request, setRequest] = useState<VisibleRangeProfileRequest | null>(null)

  useEffect(() => {
    if (!enabled || !isChartReady || !widget) {
      setRequest(null)
      return
    }

    const chart = widget.activeChart()
    let debounceId: ReturnType<typeof setTimeout> | null = null

    const capture = () => {
      let next: VisibleRangeProfileRequest | null = null
      try {
        const pane = chart.getPanes()[0]
        const priceRange = includePriceRange
          ? (pane?.getMainSourcePriceScale()?.getVisiblePriceRange() ?? null)
          : null
        next = buildVisibleRangeProfileRequest({
          exchange,
          symbol,
          timeRange: chart.getVisibleRange(),
          priceRange,
          buckets: Math.max(
            1,
            ...settings.map((profileSettings) =>
              resolveVisibleRangeProfileBucketCount({
                settings: profileSettings,
                priceRange,
                tickSize,
              })
            )
          ),
        })
      } catch {}

      setRequest((current) => (requestKey(current) === requestKey(next) ? current : next))
    }

    const scheduleCapture = () => {
      if (debounceId) clearTimeout(debounceId)
      debounceId = setTimeout(capture, RANGE_DEBOUNCE_MS)
    }

    capture()
    const visibleRangeSubscription = chart.onVisibleRangeChanged()
    visibleRangeSubscription.subscribe(null, scheduleCapture)
    const priceRangePoll = setInterval(scheduleCapture, PRICE_RANGE_POLL_MS)

    return () => {
      if (debounceId) clearTimeout(debounceId)
      clearInterval(priceRangePoll)
      try {
        visibleRangeSubscription.unsubscribe(null, scheduleCapture)
      } catch {}
    }
  }, [enabled, exchange, includePriceRange, isChartReady, settings, symbol, tickSize, widget])

  return useVisibleRangeProfile(request, enabled)
}
